// @todo: Migrate to 'DropdownMenu' from @wandb/components. For more details see:
// https://wandb.github.io/components/?path=/docs/migration-guide--docs
// eslint-disable-next-line wandb/no-deprecated-imports
import * as DropdownMenu from '@wandb/weave/components/DropdownMenu';
// @todo: Migrate to 'Icon' from @wandb/components. For more details see:
// https://wandb.github.io/components/?path=/docs/migration-guide--docs
// eslint-disable-next-line wandb/no-deprecated-imports
import {Icon} from '@wandb/weave/components/Icon';
import React, {useCallback, useContext, useState} from 'react';

import {PanelBankUpdaterContext} from '../../../state/panelbank/context';
import {RunQueryContext} from '../../../state/runs/context';
import * as ViewHooks from '../../../state/views/hooks';
import * as PanelBankSectionConfigActions from '../../../state/views/panelBankSectionConfig/actions';
import type * as PanelBankSectionConfigTypes from '../../../state/views/panelBankSectionConfig/types';
import {isMobile} from '../../../util/panelbankFlow';
import {type ImageExportType} from '../../../util/panelExport';
import {type getPanelSpec} from '../../../util/panelTypes';
import {usePanelsSharedRefsContext} from '../../Views/contexts/PanelsSharedRefsContext';
import {PanelDeleteMenuItem} from '../common/PanelDeleteMenuItem';
import {PanelDuplicateMenuItem} from '../common/PanelDuplicateMenuItem';
import {PanelOverflowMenu} from '../common/PanelOverflowMenu';
import {useSectionsForMovePanel} from '../hooks/useSectionsForMovePanel';
import {usePanelContext} from '../PanelContextProvider';
import {PanelExportMenu} from '../PanelExportModal/PanelExportMenu';
import {PanelExportModalContainer} from '../PanelExportModal/PanelExportModalContainer';
import {type ExportType} from '../types';
import {MovePanelSubmenu} from './MovePanelSubmenu';
import {
  PanelShareInReportMenuItem,
  PanelShareInReportModalContainer,
} from './PanelShareInReportMenuItem';
import {WorkspacePanelCopyLinkMenuItem} from './WorkspacePanelCopyLinkMenuItem';

type WorkspacePanelOverflowMenuProps = {
  defaultMoveToSectionRef?: PanelBankSectionConfigTypes.Ref;
  panelSpec: ReturnType<typeof getPanelSpec>;
  panelBankSectionConfigRef: PanelBankSectionConfigTypes.Ref;
  setIsOpen: React.Dispatch<React.SetStateAction<boolean>>;
  isOpen: boolean;
};

export const WorkspacePanelOverflowMenu = ({
  defaultMoveToSectionRef,
  panelSpec,
  panelBankSectionConfigRef,
  setIsOpen,
  isOpen,
}: WorkspacePanelOverflowMenuProps) => {
  const runsContext = useContext(RunQueryContext);
  const {panelBankConfigRef, workspaceSettingsRef} =
    usePanelsSharedRefsContext();
  const {panelRef, trackPanelControls, isReadOnly, isProjectReadOnly} =
    usePanelContext();

  const [exportType, setExportType] = useState<ExportType>();
  const [imageExportType, setImageExportType] = useState<ImageExportType>();
  const unsetExportType = useCallback(() => setExportType(undefined), []);

  const [reportID, setReportID] = useState<undefined | string>();
  const [reportName, setReportName] = useState('');

  const sectionsForMovePanel = useSectionsForMovePanel();
  const isMovePanelAllowed =
    runsContext.report == null && sectionsForMovePanel.length > 0;

  const duplicatePanel = ViewHooks.useViewAction(
    panelBankSectionConfigRef,
    PanelBankSectionConfigActions.duplicatePanel
  );

  const deletePanel = ViewHooks.useViewAction(
    panelBankSectionConfigRef,
    PanelBankSectionConfigActions.deletePanel
  );
  const onRemovePanel = useCallback(() => {
    deletePanel(panelRef, panelBankConfigRef, workspaceSettingsRef);
    trackPanelControls({action: 'Panel deleted'});
  }, [
    deletePanel,
    panelRef,
    panelBankConfigRef,
    workspaceSettingsRef,
    trackPanelControls,
  ]);

  const onDuplicatePanel = useCallback(() => {
    duplicatePanel(panelRef, panelBankConfigRef);
    trackPanelControls({action: 'Panel duplicated'});
  }, [duplicatePanel, panelRef, panelBankConfigRef, trackPanelControls]);

  const {setExportingPanelRefID} = useContext(PanelBankUpdaterContext);

  if (isMobile()) {
    return null;
  }

  return (
    <>
      <PanelExportModalContainer
        exportType={exportType}
        imageExportType={imageExportType}
        panelType={panelSpec.type}
        unsetExportType={unsetExportType}
      />
      {reportID && (
        <PanelShareInReportModalContainer
          reportID={reportID}
          reportName={reportName}
          setReportID={setReportID}
        />
      )}
      <PanelOverflowMenu isMenuOpen={isOpen} setIsMenuOpen={setIsOpen}>
        <DropdownMenu.Content
          // z-index of 6 needed here to exceed resizeable workspace panels,
          // which go to z-5 when they are hovered. Previously this was z-2 to
          // work with reports, somehow. z-6 seems safe.
          className="z-[6]"
          align="start">
          {!isProjectReadOnly && (
            <DropdownMenu.Item
              data-test="add-to-report-button"
              onClick={() => {
                trackPanelControls({
                  action: 'Add to report clicked',
                });
                setExportingPanelRefID(panelRef.id);
              }}>
              <Icon name="add-to-report" /> Add panel to report
            </DropdownMenu.Item>
          )}
          <WorkspacePanelCopyLinkMenuItem
            panelBankSectionConfigRef={panelBankSectionConfigRef}
          />
          <PanelExportMenu
            panelSpec={panelSpec}
            setExportType={setExportType}
            setImageExportType={setImageExportType}
          />
          {/* Share panel */}
          {!isReadOnly && !isProjectReadOnly && !isMobile() && (
            <PanelShareInReportMenuItem
              setReportID={setReportID}
              setReportName={setReportName}
            />
          )}
          <DropdownMenu.Separator />
          <PanelDuplicateMenuItem onSelect={onDuplicatePanel} />
          {isMovePanelAllowed &&
            panelBankConfigRef &&
            panelBankSectionConfigRef && (
              <MovePanelSubmenu
                panelBankConfigRef={panelBankConfigRef}
                panelBankSectionConfigRef={panelBankSectionConfigRef}
                eligibleSections={sectionsForMovePanel}
                defaultMoveToSectionRef={defaultMoveToSectionRef}
              />
            )}
          <DropdownMenu.Separator />
          <PanelDeleteMenuItem onSelect={onRemovePanel} />
        </DropdownMenu.Content>
      </PanelOverflowMenu>
    </>
  );
};
